import React, { useMemo, useState } from 'react';
import { FilterIcon, ClockIcon, XIcon } from 'lucide-react';
import type { HistoricalRecord, MetricType } from '../../types';
import { Header } from '../layout/Header';
import { HistoryTable } from './HistoryTable';
import { SearchableSelect } from '../ui/SearchableSelect';
import { getHistoricalRecords, getAllDivisionsForSelection, getAllTeamsForSelection, getAllLoanOfficersForSelection } from '../../data/mockData';
type EntityFilter = 'all' | 'division' | 'team' | 'loanOfficer';
type PeriodFilter = 'all' | 'weekly' | 'monthly';
type MetricFilter = 'all' | MetricType;
export function HistoryPage() {
  const allRecords = getHistoricalRecords();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [metricFilter, setMetricFilter] = useState<MetricFilter>('all');
  const [entityFilter, setEntityFilter] = useState<EntityFilter>('all');
  const [specificEntityId, setSpecificEntityId] = useState<string>('');
  // Get options for specific entity selection
  const entityOptions = useMemo(() => {
    if (entityFilter === 'division') {
      return getAllDivisionsForSelection();
    }
    if (entityFilter === 'team') {
      return getAllTeamsForSelection();
    }
    if (entityFilter === 'loanOfficer') {
      return getAllLoanOfficersForSelection();
    }
    return [];
  }, [entityFilter]);
  const handleEntityFilterChange = (newFilter: EntityFilter) => {
    setEntityFilter(newFilter);
    // Reset specific entity selection when entity type changes
    setSpecificEntityId('');
  };
  const handleClearSpecificEntity = () => {
    setSpecificEntityId('');
  };
  const filteredRecords = useMemo(() => {
    return allRecords.filter((record: HistoricalRecord) => {
      if (periodFilter !== 'all' && record.periodType !== periodFilter) return false;
      if (metricFilter !== 'all' && record.metric !== metricFilter) return false;
      if (entityFilter !== 'all' && record.entityType !== entityFilter) return false;
      // Filter by specific entity ID if selected
      if (specificEntityId && record.entityId !== specificEntityId) return false;
      return true;
    });
  }, [allRecords, periodFilter, metricFilter, entityFilter, specificEntityId]);
  const achievedCount = filteredRecords.filter((r: HistoricalRecord) => r.achieved).length;
  const missedCount = filteredRecords.filter((r: HistoricalRecord) => !r.achieved).length;
  const getEntityLabel = () => {
    if (entityFilter === 'division') return 'Division';
    if (entityFilter === 'team') return 'Team';
    if (entityFilter === 'loanOfficer') return 'Loan Officer';
    return 'Entity';
  };
  return <div className="min-h-screen" data-id="element-194">
      <Header title="Historical Records" subtitle="Review past goal achievement across all periods and entities" data-id="element-195" />

      <div className="px-6 lg:px-8 py-6 space-y-6" data-id="element-196">
        {/* Summary Stats */}
        <div className="flex items-center gap-6 flex-wrap" data-id="element-197">
          <div className="flex items-center gap-2.5" data-id="element-198">
            <ClockIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-199" />
            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-200">
              <span className="font-semibold text-gray-900 dark:text-white" data-id="element-201">
                {filteredRecords.length}
              </span>{' '}
              records
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-202">
            <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-203" />
            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-204">
              <span className="font-semibold text-emerald-700 dark:text-emerald-400" data-id="element-205">
                {achievedCount}
              </span>{' '}
              achieved
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-206">
            <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-207" />
            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-208">
              <span className="font-semibold text-red-700 dark:text-red-400" data-id="element-209">
                {missedCount}
              </span>{' '}
              missed
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 transition-colors" data-id="element-210">
          <div className="flex items-center gap-2.5 mb-4" data-id="element-211">
            <FilterIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-212" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300" data-id="element-213">
              Filters
            </span>
          </div>

          {/* First Row: Basic Filters */}
          <div className="flex flex-wrap gap-4 mb-4" data-id="element-214">
            <div data-id="element-215">
              <label htmlFor="filter-period" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2" data-id="element-216">
                Period Type
              </label>
              <select id="filter-period" value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-217">
                <option value="all" data-id="element-218">All Periods</option>
                <option value="weekly" data-id="element-219">Weekly</option>
                <option value="monthly" data-id="element-220">Monthly</option>
              </select>
            </div>

            <div data-id="element-221">
              <label htmlFor="filter-metric" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2" data-id="element-222">
                Metric
              </label>
              <select id="filter-metric" value={metricFilter} onChange={e => setMetricFilter(e.target.value as MetricFilter)} className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-223">
                <option value="all" data-id="element-224">All Metrics</option>
                <option value="softPull" data-id="element-225">Soft Pull</option>
                <option value="submitted" data-id="element-226">Submitted</option>
              </select>
            </div>

            <div data-id="element-227">
              <label htmlFor="filter-entity" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2" data-id="element-228">
                Entity Type
              </label>
              <select id="filter-entity" value={entityFilter} onChange={e => handleEntityFilterChange(e.target.value as EntityFilter)} className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-229">
                <option value="all" data-id="element-230">All Entities</option>
                <option value="division" data-id="element-231">Division</option>
                <option value="team" data-id="element-232">Team</option>
                <option value="loanOfficer" data-id="element-233">Loan Officer</option>
              </select>
            </div>
          </div>

          {/* Second Row: Specific Entity Filter */}
          {entityFilter !== 'all' && <div className="pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-234">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2" data-id="element-235">
                Filter by specific {getEntityLabel()}
              </label>
              <div className="flex items-center gap-3" data-id="element-236">
                <div className="flex-1 max-w-md" data-id="element-237">
                  <SearchableSelect options={entityOptions} value={specificEntityId} onChange={setSpecificEntityId} placeholder={`Select ${getEntityLabel().toLowerCase()}...`} data-id="element-238" />
                </div>
                {specificEntityId && <button onClick={handleClearSpecificEntity} className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Clear entity filter" data-id="element-239">
                    <XIcon className="w-4 h-4" data-id="element-240" />
                    Clear
                  </button>}
              </div>
            </div>}
        </div>

        {/* Table */}
        <HistoryTable records={filteredRecords} data-id="element-241" />
      </div>
    </div>;
}